import React, { Children, isValidElement, useContext } from 'react';
import {
  Collection,
  CollectionRendererContext,
  ListBoxContext,
  ListBoxProps as AriaListBoxProps,
  ListBoxSectionProps,
  ListStateContext,
  UNSTABLE_CollectionBuilder,
  UNSTABLE_createBranchComponent,
  useContextProps,
} from 'react-aria-components';
import { useFocusRing } from '@react-aria/focus';
import { useListBox, useListBoxSection } from '@react-aria/listbox';
import { filterDOMProps, mergeProps } from '@react-aria/utils';
import {
  useListState,
  type Collection as AriaCollection,
  type ListState,
  type Node,
} from 'react-stately';
import ListBoxItem from '../ListBoxItem';
import styles from './ListBox.module.css';

export interface ListBoxProps<T> extends AriaListBoxProps<T> {
  isLoading?: boolean;
}

const GroupHeader = (_props: { children: React.ReactNode }) => null;

// TODO: Remove RAC unstable collection APIs when ComboBox owns the Alto collection implementation.
const ListBoxGroup = UNSTABLE_createBranchComponent<
  object,
  ListBoxSectionProps<object>,
  HTMLElement
>('section', function ListBoxGroup(props, ref, section) {
  const state = useContext(ListStateContext)!;
  const { CollectionBranch } = useContext(CollectionRendererContext);
  const groupChildren =
    typeof props.children === 'function'
      ? []
      : Children.toArray(props.children);
  const heading = groupChildren.find(
    (child) => isValidElement(child) && child.type === GroupHeader,
  );
  const headingContent = isValidElement<{ children?: React.ReactNode }>(heading)
    ? heading.props.children
    : undefined;
  const { groupProps, headingProps } = useListBoxSection({
    heading: headingContent,
    'aria-label': props['aria-label'],
  });

  return (
    <section
      {...filterDOMProps(props as Parameters<typeof filterDOMProps>[0])}
      {...groupProps}
      ref={ref}
      className={props.className}
      style={props.style}
    >
      {headingContent && (
        <div {...headingProps} className={styles['list-box__group-header']}>
          {headingContent}
        </div>
      )}
      <CollectionBranch collection={state.collection} parent={section} />
    </section>
  );
}) as <T extends object>(
  props: ListBoxSectionProps<T> & React.RefAttributes<HTMLElement>,
) => React.ReactElement | null;

interface ListBoxRootProps extends ListBoxProps<object> {
  listState: ListState<object>;
  listBoxRef: React.RefObject<HTMLDivElement | null>;
}

const ListBoxRoot = ({
  listState: state,
  listBoxRef,
  className,
  isLoading: _isLoading,
  onScroll,
  slot,
  style,
  ...ariaProps
}: ListBoxRootProps) => {
  const { CollectionRoot } = useContext(CollectionRendererContext);
  const { listBoxProps } = useListBox(ariaProps, state, listBoxRef);
  const { focusProps, isFocused, isFocusVisible } = useFocusRing();
  const renderProps = {
    isEmpty: state.collection.size === 0,
    isFocused,
    isFocusVisible,
    isDropTarget: false,
    layout: ariaProps.layout ?? 'stack',
    state,
  };
  const resolvedClassName =
    typeof className === 'function'
      ? className({ ...renderProps, defaultClassName: undefined })
      : className;
  const resolvedStyle =
    typeof style === 'function'
      ? style({ ...renderProps, defaultStyle: {} })
      : style;

  return (
    <div
      {...filterDOMProps(ariaProps)}
      {...mergeProps(listBoxProps, focusProps)}
      ref={listBoxRef as React.RefObject<HTMLDivElement>}
      className={[styles['list-box'], resolvedClassName]
        .filter(Boolean)
        .join(' ')}
      onScroll={onScroll}
      slot={slot ?? undefined}
      style={resolvedStyle}
      data-empty={state.collection.size === 0 || undefined}
      data-focused={isFocused || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-layout={ariaProps.layout ?? 'stack'}
      data-orientation={ariaProps.orientation ?? 'vertical'}
    >
      <ListStateContext.Provider value={state}>
        <CollectionRoot collection={state.collection} scrollRef={listBoxRef} />
      </ListStateContext.Provider>
      {state.collection.size === 0 && (
        <div
          role="option"
          aria-selected="false"
          aria-disabled="true"
          className={styles['list-box__empty-state']}
        >
          {ariaProps.renderEmptyState?.(renderProps) ?? 'No results found.'}
        </div>
      )}
    </div>
  );
};

interface StandaloneListBoxProps<T extends object> {
  props: ListBoxProps<T>;
  collection: AriaCollection<Node<T>>;
  listBoxRef: React.RefObject<HTMLDivElement | null>;
}

const StandaloneListBox = <T extends object>({
  props,
  collection,
  listBoxRef,
}: StandaloneListBoxProps<T>) => {
  const state = useListState({
    ...props,
    collection,
    children: undefined,
    items: undefined,
  });

  return (
    <ListBoxRoot
      {...(props as unknown as ListBoxProps<object>)}
      listState={state as ListState<object>}
      listBoxRef={listBoxRef}
    />
  );
};

export const ListBox = <T extends object>(inputProps: ListBoxProps<T>) => {
  const [contextProps, listBoxRef] = useContextProps(
    inputProps,
    null,
    ListBoxContext,
  );
  const sharedState = useContext(ListStateContext) as ListState<T> | null;
  const props = {
    ...contextProps,
    children: contextProps.isLoading ? (
      <ListBoxItem isLoading textValue="Loading" />
    ) : (
      contextProps.children
    ),
  };

  if (sharedState) {
    return (
      <ListBoxRoot
        {...(props as unknown as ListBoxProps<object>)}
        listState={sharedState as ListState<object>}
        listBoxRef={listBoxRef}
      />
    );
  }

  return (
    // TODO: Replace with the stable Alto collection boundary during the ComboBox refactor.
    <UNSTABLE_CollectionBuilder content={<Collection {...props} />}>
      {(collection) => (
        <StandaloneListBox
          props={props}
          collection={collection as unknown as AriaCollection<Node<T>>}
          listBoxRef={listBoxRef}
        />
      )}
    </UNSTABLE_CollectionBuilder>
  );
};

ListBox.Item = ListBoxItem;
ListBox.Group = ListBoxGroup;
(ListBoxGroup as React.ComponentType).displayName = 'ListBox.Group';
ListBox.GroupHeader = GroupHeader;
GroupHeader.displayName = 'ListBox.GroupHeader';
(ListBox.Item as React.ComponentType).displayName = 'ListBox.Item';

export default ListBox;
